import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Loader2, MapPin, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createTicket } from "../api/supportApi";
import { RichComposer } from "../components/RichComposer";
import { KnowledgeBaseSuggestions } from "../components/KnowledgeBaseSuggestions";
import { EntityPreview } from "../components/EntityPreview";
import { CategoryOption, PriorityOption, categoryLabel, priorityLabel } from "../components/meta";
import { useTicketPageContext } from "./useTicketPageContext";
import {
  TICKET_CATEGORIES,
  TICKET_PRIORITIES,
  type TicketCategory,
  type TicketPriority,
} from "../types";

type T = Record<string, string>;

export function WidgetCompose({
  onBack,
  onCreated,
  t,
}: {
  onBack: () => void;
  onCreated: (ticketNo: string) => void;
  t: T;
}) {
  const queryClient = useQueryClient();
  const pageContext = useTicketPageContext();
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState<TicketCategory>("Technical");
  const [priority, setPriority] = useState<TicketPriority>("Medium");
  const [bodyHtml, setBodyHtml] = useState("");
  const [bodyText, setBodyText] = useState("");

  const mutation = useMutation({
    mutationFn: () =>
      createTicket({ subject, category, priority, bodyHtml, body: bodyText, context: pageContext }),
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ["support", "tickets"] });
      onCreated(ticket.name);
    },
  });

  const canSubmit =
    subject.trim().length > 2 && bodyText.trim().length > 2 && !mutation.isPending;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-gray-100 px-2 py-2 dark:border-gray-800">
        <button
          onClick={onBack}
          className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{t.composeTitle}</p>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto p-3">
        {/* Auto-detected page/record context — attached to the ticket */}
        <div className="overflow-hidden rounded-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/40">
          <div className="flex items-center gap-1.5 px-3 pt-2.5 text-xs font-medium text-gray-700 dark:text-gray-300">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" />
            {t.contextTitle}
          </div>
          <div className="px-3 py-2.5">
            {pageContext.entity && <EntityPreview entity={pageContext.entity} url={pageContext.url} t={t} />}
          </div>
          <p className="border-t border-gray-200 dark:border-gray-700 px-3 py-2 text-[11px] leading-snug text-gray-400 dark:border-gray-800 dark:text-gray-500">
            {t.contextHint}
          </p>
        </div>

        <div className="space-y-1.5">
          <Input
            placeholder={t.subjectPlaceholder}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            autoFocus
            className="h-11 rounded-sm border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900/40"
          />
          <KnowledgeBaseSuggestions query={subject} />
        </div>

        <div className="divide-y divide-border/40 overflow-hidden rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/40">
          <div className="flex items-center justify-between gap-2 px-3 py-2.5">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t.categoryLabel}
            </span>
            <Select value={category} onValueChange={(v) => setCategory(v as TicketCategory)}>
              <SelectTrigger className="min-w-[8rem] justify-end">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TICKET_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    <CategoryOption category={c} label={categoryLabel(t, c)} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between gap-2 px-3 py-2.5">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t.priorityLabel}
            </span>
            <Select value={priority} onValueChange={(v) => setPriority(v as TicketPriority)}>
              <SelectTrigger className="min-w-[8rem] justify-end">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TICKET_PRIORITIES.map((p) => (
                  <SelectItem key={p} value={p}>
                    <PriorityOption priority={p} label={priorityLabel(t, p)} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <RichComposer
          minHeightClass="min-h-[110px]"
          className="rounded-sm"
          placeholder={t.descriptionPlaceholder}
          onChange={(html, text) => {
            setBodyHtml(html);
            setBodyText(text);
          }}
        />
      </div>

      <div className="border-t border-gray-100 p-3 dark:border-gray-800">
        <Button
          className="w-full rounded-full"
          onClick={() => mutation.mutate()}
          disabled={!canSubmit}
        >
          {mutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
          {t.sendRequest}
        </Button>
      </div>
    </div>
  );
}
